import React, { useEffect, useState, useRef } from 'react';
import RangeSliderRefinement from './RangeSliderRefinement';
import { CheckboxRefinement } from './CheckboxRefinement';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { ToggleButtonRefinement } from './ToggleButtonRefinement/ToggleButtonRefinement';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { styles } from './CustomRefinementListContainer.css';
import { Swiper } from 'swiper';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

interface CustomRefinementListContainerProps {
  initialFacets: Array<{
    facet: string;
    type: string;
    title?: string;
  }>;
  priceRange: {
    min: number;
    max: number;
  };
  saleItems: boolean | string;
  mobileShow: boolean;
  setMobileShowFilter: (mobileShow: boolean) => void;
  nbHits: number;
  nbSaleItems: number;
  trackingInfo: any;
  isSearchPage?: boolean;
  isSticky?: boolean;
}

const CustomRefinementListContainer = ({
  initialFacets,
  priceRange,
  saleItems,
  mobileShow,
  setMobileShowFilter,
  nbHits,
  nbSaleItems,
  trackingInfo,
  isSearchPage,
  isSticky,
}: CustomRefinementListContainerProps) => {
  const [disableScroll, setDisableScroll] = useState(false);
  const [facets, setFacets] = useState(initialFacets);
  const [openFilterIndex, setOpenFilterIndex] = useState<number | null>(null);
  const isDesktop = useBreakpoint('lg', 'up');
  const [swiper, setSwiper] = useState<Swiper | null>(null);

  useEffect(() => {
    setFacets([...initialFacets]);
  }, [initialFacets]);

  // Remove scrolling on mobile.
  useEffect(() => {
    if (mobileShow) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [mobileShow]);

  useEffect(() => {
    if (!swiper) {
      return;
    }
    swiper.update();
    if (disableScroll) {
      swiper.disable();
    } else {
      swiper.enable();
    }
  }, [swiper, nbHits, disableScroll]);

  const sortedFacets = [...facets].sort(a => (a.type === 'toggleRefinement' ? -1 : 1)); // Place toggle buttons at the front of the slider.

  const onToggle = (facet: string, isChecked: boolean) => {
    if (facet !== 'isMarketplace') return;

    pushToDataLayer({
      event: isChecked ? 'select_filter' : 'unselect_filter',
      filter_group: 'Exclude Marketplace products',
      filter_name: 'undefined',
      filter_type: 'toggle',
      filter_value: 'N',
      search_type: isSearchPage ? 'site search' : 'undefined',
      search_term: 'undefined',
    });

    if (isChecked) {
      setFacets(prev => prev.filter(f => f.facet !== 'Sold by'));
      return;
    }

    setFacets([...initialFacets]);
  };

  useEffect(() => {
    if (!swiper || openFilterIndex === null) {
      return;
    }
    swiper.slideTo(openFilterIndex);
  }, [openFilterIndex, swiper, nbHits]);

  const filters = sortedFacets.map(({ facet, type, title }, index) =>
    type === 'rangeInput' || facet === 'Price' ? (
      <RangeSliderRefinement
        key={index}
        attribute={`facets.${facet}`}
        defaultRefinement={priceRange}
        facet={facet}
        trackingInfo={{ ...trackingInfo, position: index + 1 }}
        nbHits={nbHits}
        saleItems={saleItems}
        nbSaleItems={nbSaleItems}
        setDisableScroll={setDisableScroll}
        onDropdownToggle={isOpen => setOpenFilterIndex(isOpen ? index : null)}
        isSearchPage={isSearchPage}
      />
    ) : type === 'toggleRefinement' ? (
      <ToggleButtonRefinement
        key={index}
        facet={facet}
        valueWhenChecked={false}
        label={title ?? facet}
        onToggle={isChecked => onToggle(facet, isChecked)}
        isSearchPage={isSearchPage}
      />
    ) : (
      <CheckboxRefinement
        key={index}
        facet={facet}
        trackingInfo={{ ...trackingInfo, position: index + 1 }}
        limit={100}
        showMoreLimit={25}
        setDisableScroll={setDisableScroll}
        onDropdownToggle={isOpen => setOpenFilterIndex(isOpen ? index : null)}
      />
    )
  );
  // Render the slider.
  return (
    <div className={`facets-container ${mobileShow ? ' mobile-active' : ''}`} id="facets-container">
      {!isSearchPage || !isDesktop ? (
        <>
          <button className="mobile-close" onClick={() => setMobileShowFilter(false)} />
          <h3 className="mobile-title">Filter</h3>
        </>
      ) : null}
      {isDesktop && !isSearchPage ? (
        <Carousel
          onSwiper={swiper => {
            setSwiper(swiper);
          }}
          showPagination={false}
          showProgressBar={false}
          showArrows={true}
          arrowPosition="middle"
          slidesPerView="auto"
          containerClassName={styles.container({ isSticky })}
          allowTouchMove={!disableScroll}
          slideArrowClassName={styles.slideArrow}
          arrowBackgroundColor={isSticky ? palette.secondary.main : palette.primary.light}
          hideArrowBackgroundColor={false}
        >
          {filters}
        </Carousel>
      ) : (
        <>{filters}</>
      )}
      <div className={styles.bottomHide} />
      <Button
        classes="ais-ShowResults mobile-show"
        type="primary"
        onClick={() => setMobileShowFilter(false)}
        label="Show results"
      />
    </div>
  );
};

export default CustomRefinementListContainer;
